import { Instant, LocalDate, LocalDateTime } from "@js-joda/core";
import type { PatientProfilePatient } from "../../../../../patientProfileModal.types";
import { dateFormatter } from "../../../../../../../shared/utils/date-formatter";

export function formatClinicalSeverity(value: PatientProfilePatient["clinicalSeverityLevel"]) {
  switch (value) {
    case "MINIMAL_ASSISTANCE":
      return "Minimal Assistance";
    case "WALKING_ASSISTANCE":
      return "Medium Assistance";
    case "HIGHEST_ASSISTANCE":
      return "Highest Assistance";
    case null:
      return "";
  }
}

export function formatSummaryValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value instanceof LocalDate) {
    return dateFormatter.toDate(value);
  }

  if (value instanceof LocalDateTime || value instanceof Instant) {
    return dateFormatter.toDateTime(value);
  }

  return String(value);
}

export function formatSsn(value: string | null) {
  if (!value) {
    return "";
  }

  const trimmedValue = value.toString().trim().replace(/^\+/, "");

  if (/[^0-9]/.test(trimmedValue)) {
    return value;
  }

  const firstPart = trimmedValue.slice(0, 3);
  let rest = trimmedValue.slice(3);

  if (rest.length > 2) {
    rest = `${rest.slice(0, 2)}-${rest.slice(2, 6)}`;
  }

  return rest ? `${firstPart}-${rest}`.trim() : firstPart;
}

export function getPatientAddressState(patient: PatientProfilePatient) {
  const components = patient.address?.components as
    | {
        administrativeAreaLevel1?: string;
        formatedAddressDetails?: {
          state?: string;
        };
      }
    | undefined;

  return components?.formatedAddressDetails?.state ?? components?.administrativeAreaLevel1 ?? null;
}

export function getPatientCountyName(patient: PatientProfilePatient) {
  const components = patient.address?.components as
    | {
        administrativeAreaLevel2?: string;
        formatedAddressDetails?: {
          county?: string;
        };
      }
    | undefined;

  return components?.formatedAddressDetails?.county ?? components?.administrativeAreaLevel2 ?? "";
}

function hasPatientAddressComponents(patient: PatientProfilePatient) {
  return patient.address?.components !== undefined && patient.address.components !== null;
}

export function shouldShowMissingAddressWarning(
  patient: PatientProfilePatient,
  showBillingFeature: boolean
) {
  return (
    showBillingFeature &&
    hasPatientAddressComponents(patient) &&
    patient.missingAddressFields !== undefined &&
    patient.missingAddressFields.length > 0
  );
}

export function formatLocationGroup(patient: PatientProfilePatient) {
  return patient.locationGroupName || "";
}

export function formatDraftDateValue(value: string) {
  return value === "" ? "" : dateFormatter.toDate(LocalDate.parse(value));
}

export function formatArizonaContingencyPlan(
  value: PatientProfilePatient["arizonaContingencyPlanCode"]
) {
  switch (value) {
    case "A":
      return "Needs services within two hours";
    case "B":
      return "Needs services today";
    case "C":
      return "Needs services within 48 hours";
    case "D":
      return "Can wait until next scheduled visit by provider";
    default:
      return formatSummaryValue(value);
  }
}
